import { ResizeComponent } from '@iva/resize-component';
import React, { useRef, useEffect } from 'react';
import * as d3 from 'd3';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

var SpinnerFc = function (_a) {
    var _b = _a.duration, duration = _b === void 0 ? 1000 : _b, _c = _a.delay, delay = _c === void 0 ? 250 : _c, _d = _a.animate, animate = _d === void 0 ? true : _d, _e = _a.invisible, invisible = _e === void 0 ? 100 : _e, _f = _a.visible, visible = _f === void 0 ? 1000 : _f, size = _a.size, content = _a.content;
    /* The useRef Hook creates a variable that "holds on" to a value across rendering
     passes. In this case it will hold our component's SVG DOM element. It's
     initialized null and React will assign it later (see the return statement) */
    var d3Container = useRef(null);
    /**
     * This useEffect block is run only once after the component mounts. We will do the initial D3 setup here.
     */
    useEffect(function () {
        if (d3Container.current) {
            // extract the paths from the svg document
            var paths = content.documentElement.querySelectorAll("path");
            if (!paths || paths.length === 0) {
                return;
            }
            // clear the screen ;)
            var svg = d3.select(d3Container.current);
            svg.selectAll().remove();
            // attach paths into a group Container
            var groupContainer = svg.append("g");
            var rawNode_1 = groupContainer.node();
            if (!rawNode_1) {
                return;
            }
            paths.forEach(function (p) {
                rawNode_1.append(p);
            });
            // use groupContainer bounding box to set svg size
            var bBox = rawNode_1.getBBox();
            svg.attr("viewBox", "".concat(bBox.x, " ").concat(bBox.y, " ").concat(bBox.width, " ").concat(bBox.height));
            if (!animate) {
                // no animation? we are done.
                return;
            }
            // animate:
            var ticklength_1 = duration;
            var timeVisible_1 = visible;
            var timeInvisible_1 = invisible;
            svg
                .selectAll("path")
                .attr("fill-opacity", 0)
                .transition()
                .delay(function (_, i) { return i * delay; })
                .on("start", function repeat() {
                var active = d3.active(this);
                if (active) {
                    active
                        .attr("fill-opacity", 0)
                        .transition()
                        .ease(d3.easeCubicInOut)
                        .duration(ticklength_1)
                        .attr("fill-opacity", 1)
                        .transition()
                        .ease(d3.easeCubicInOut)
                        .delay(timeVisible_1)
                        .duration(ticklength_1)
                        .attr("fill-opacity", 0)
                        .transition()
                        .delay(timeInvisible_1)
                        .on("start", repeat);
                }
                else {
                    console.warn("Unable to start transition");
                }
            });
        }
    }, 
    /* The empty dependency array of useEffect. This block will run only once after mount. */
    [content, duration, visible, invisible, animate, delay]);
    /**
     * Returns the width, height of the actual chart drawing area (total SVG size minus margins)
     */
    return (React.createElement("svg", { width: size.width, height: size.height, ref: d3Container, preserveAspectRatio: "xMidYMid meet", xmlns: "http://www.w3.org/2000/svg", version: "1.1" }));
};
var ControlledSpinner = ResizeComponent(SpinnerFc);

var stripesBlack = "<svg version=\"1.1\" id=\"Ebene_1\" xmlns=\"http://www.w3.org/2000/svg\" xmlns:xlink=\"http://www.w3.org/1999/xlink\" x=\"0px\" y=\"0px\"\nwidth=\"27.579px\" height=\"27.72px\" viewBox=\"0 0 27.579 27.72\" enable-background=\"new 0 0 27.579 27.72\" xml:space=\"preserve\">\n<g>\n<path d=\"M0,27.705c6.561-3.378,16.172-8.837,27.579-16.821v-2.68C17.54,14.772,7.329,20.047,0,23.536V27.705z\"/>\n<path d=\"M0,19.703c7.305-2.976,17.515-7.574,27.579-13.56V4.82C18.12,9.513,8.293,12.449,0,14.28V19.703z\"/>\n<path d=\"M27.579,27.72V14.24c-6.279,5.293-12.496,9.876-17.72,13.479H27.579z\"/>\n<path d=\"M6.332,27.72c4.959-2.944,10.98-6.787,16.976-11.279c1.399-1.047,2.827-2.127,4.271-3.243v-1.67\n   C18.706,18.181,9.647,23.726,2.571,27.72H6.332L6.332,27.72z\"/>\n<path d=\"M0,12.321c8.067-1.419,17.936-3.971,27.579-8.554V2.265C17.861,6.627,8.134,8.477,0,9.136V12.321z\"/>\n<path d=\"M0,0v7.721c0.603-0.037,1.217-0.08,1.844-0.129c6.707-0.516,15.857-1.701,25.734-5.971V0H0z\"/>\n</g>\n</svg>\n";
var stripesWhite = "<svg version=\"1.1\" id=\"Ebene_1\" xmlns=\"http://www.w3.org/2000/svg\" xmlns:xlink=\"http://www.w3.org/1999/xlink\" x=\"0px\" y=\"0px\" width=\"27.579px\" height=\"27.72px\" viewBox=\"0 0 27.579 27.72\" enable-background=\"new 0 0 27.579 27.72\" xml:space=\"preserve\">\n<path fill=\"#FFFFFF\" d=\"M0,27.705c6.561-3.378,16.172-8.837,27.579-16.821v-2.68C17.54,14.772,7.329,20.047,0,23.536V27.705z\" />\n<path fill=\"#FFFFFF\" d=\"M0,19.703c7.305-2.976,17.515-7.574,27.579-13.56V4.82C18.12,9.513,8.293,12.449,0,14.28V19.703z\" />\n<path fill=\"#FFFFFF\" d=\"M27.579,27.72V14.24c-6.279,5.293-12.496,9.876-17.72,13.479H27.579z\" />\n<path fill=\"#FFFFFF\" d=\"M6.332,27.72c4.959-2.944,10.98-6.787,16.976-11.279c1.399-1.047,2.827-2.127,4.271-3.243v-1.67\n    C18.706,18.181,9.647,23.726,2.571,27.72H6.332L6.332,27.72z\" />\n<path fill=\"#FFFFFF\" d=\"M0,12.321c8.067-1.419,17.936-3.971,27.579-8.554V2.265C17.861,6.627,8.134,8.477,0,9.136V12.321z\" />\n<path fill=\"#FFFFFF\" d=\"M0,0v7.721c0.603-0.037,1.217-0.08,1.844-0.129c6.707-0.516,15.857-1.701,25.734-5.971V0H0z\" />\n</svg>";
var stripesGrey = "<svg version=\"1.1\" id=\"Ebene_1\" xmlns=\"http://www.w3.org/2000/svg\" xmlns:xlink=\"http://www.w3.org/1999/xlink\" x=\"0px\" y=\"0px\" width=\"27.579px\" height=\"27.72px\" viewBox=\"0 0 27.579 27.72\" enable-background=\"new 0 0 27.579 27.72\" xml:space=\"preserve\">\n<path fill=\"#9E9D9C\" d=\"M0,27.705c6.561-3.378,16.172-8.837,27.579-16.821v-2.68C17.54,14.772,7.329,20.047,0,23.536V27.705z\" />\n<path fill=\"#9E9D9C\" d=\"M0,19.703c7.305-2.976,17.515-7.574,27.579-13.56V4.82C18.12,9.513,8.293,12.449,0,14.28V19.703z\" />\n<path fill=\"#9E9D9C\" d=\"M27.579,27.72V14.24c-6.279,5.293-12.496,9.876-17.72,13.479H27.579z\" />\n<path fill=\"#9E9D9C\" d=\"M6.332,27.72c4.959-2.944,10.98-6.787,16.976-11.279c1.399-1.047,2.827-2.127,4.271-3.243v-1.67\n    C18.706,18.181,9.647,23.726,2.571,27.72H6.332L6.332,27.72z\" />\n<path fill=\"#9E9D9C\" d=\"M0,12.321c8.067-1.419,17.936-3.971,27.579-8.554V2.265C17.861,6.627,8.134,8.477,0,9.136V12.321z\" />\n<path fill=\"#9E9D9C\" d=\"M0,0v7.721c0.603-0.037,1.217-0.08,1.844-0.129c6.707-0.516,15.857-1.701,25.734-5.971V0H0z\" />\n</svg>";
var stripesOriginal = "<svg version=\"1.1\" xmlns=\"http://www.w3.org/2000/svg\" xmlns:xlink=\"http://www.w3.org/1999/xlink\" x=\"0px\" y=\"0px\" width=\"41.508px\" height=\"41.717px\" viewBox=\"0 0 41.508 41.717\">\n<!--id=\"Ebene_1\" -->\n<!--enable-background=\"new 0 0 41.508 41.717\" xml:space=\"preserve\"-->\n<path fill=\"#179C7D\" d=\"M0,41.697c9.874-5.083,24.338-13.302,41.507-25.317v-4.033C26.396,22.233,11.031,30.171,0,35.422V41.697z\" />\n<path fill=\"#179C7D\" d=\"M0,29.654c10.994-4.479,26.36-11.399,41.507-20.408V7.255C27.271,14.317,12.48,18.737,0,21.493V29.654z\" />\n<path fill=\"#179C7D\" d=\"M41.507,41.717V21.432c-9.45,7.966-18.809,14.864-26.67,20.285H41.507z\" />\n<path fill=\"#179C7D\" d=\"M9.53,41.717c7.465-4.432,16.526-10.215,25.55-16.974c2.105-1.578,4.255-3.203,6.428-4.883v-2.514\n    C28.151,27.359,14.519,35.706,3.869,41.717H9.53z\" />\n<path fill=\"#179C7D\" d=\"M0,18.542c12.14-2.135,26.993-5.976,41.507-12.874V3.407C26.881,9.973,12.242,12.757,0,13.748V18.542z\" />\n<path fill=\"#179C7D\" d=\"M0,0v11.62c0.907-0.056,1.831-0.121,2.775-0.194c10.093-0.776,23.867-2.56,38.732-8.987V0H0z\" />\n<path fill=\"#FFFFFF\" d=\"M0,11.62v2.128c12.242-0.991,26.881-3.775,41.507-10.341V2.439C26.643,8.866,12.868,10.65,2.775,11.426\n            C1.831,11.5,0.907,11.564,0,11.62z\" />\n<path fill=\"#FFFFFF\" d=\"M0,21.493c12.48-2.756,27.271-7.176,41.507-14.238V5.669C26.993,12.566,12.14,16.407,0,18.542V21.493z\" />\n<path fill=\"#FFFFFF\" d=\"M0,35.422c11.031-5.251,26.396-13.189,41.507-23.075V9.246C26.36,18.255,10.994,25.175,0,29.654V35.422z\" />\n<path fill=\"#FFFFFF\" d=\"M9.53,41.717h5.308c7.861-5.42,17.22-12.319,26.67-20.285V19.86c-2.173,1.68-4.322,3.305-6.428,4.883\n            C26.056,31.502,16.995,37.285,9.53,41.717z\" />\n<path fill=\"#FFFFFF\" d=\"M0,41.717h3.869c10.65-6.011,24.282-14.357,37.638-24.371V16.38C24.338,28.396,9.874,36.614,0,41.697\n            V41.717z\" />\n</svg>";

var BlackStripesSpinner = function (props) { return (React.createElement(ControlledSpinner, __assign({ content: new DOMParser().parseFromString(stripesBlack, "image/svg+xml") }, props))); };
var WhiteStripesSpinner = function (props) { return (React.createElement(ControlledSpinner, __assign({ content: new DOMParser().parseFromString(stripesWhite, "image/svg+xml") }, props))); };
var GreyStripesSpinner = function (props) { return (React.createElement(ControlledSpinner, __assign({ content: new DOMParser().parseFromString(stripesGrey, "image/svg+xml") }, props))); };
var OriginalStripesSpinner = function (props) { return (React.createElement(ControlledSpinner, __assign({ content: new DOMParser().parseFromString(stripesOriginal, "image/svg+xml") }, props))); };
var FhgSpinner = ResizeComponent(function (_a) {
    var _b = _a.type, type = _b === void 0 ? "black" : _b, props = __rest(_a, ["type"]);
    switch (type) {
        case "black":
            return React.createElement(BlackStripesSpinner, __assign({}, props));
        case "grey":
            return React.createElement(GreyStripesSpinner, __assign({}, props));
        case "original":
            return React.createElement(OriginalStripesSpinner, __assign({}, props));
        case "white":
            return React.createElement(WhiteStripesSpinner, __assign({}, props));
    }
});
/**
 * @deprecated Use FhgSpinner directly.
 */
var Spinner = FhgSpinner;

var emptySvg = new DOMParser().parseFromString("<svg/>", "image/svg+xml");
var SrcSpinner = function (_a) {
    var src = _a.src, props = __rest(_a, ["src"]);
    var _b = React.useState(emptySvg), logo = _b[0], setLogo = _b[1];
    React.useEffect(function () {
        d3.svg(src)
            .then(function (doc) { return setLogo(doc); })["catch"](function (err) { return new Error("Error  ".concat(err, " fetching logo ").concat(src)); });
    }, [src]);
    /**
     * Returns the width, height of the actual chart drawing area (total SVG size minus margins)
     */
    return React.createElement(ControlledSpinner, __assign({}, props, { content: logo }));
};

export { BlackStripesSpinner, ControlledSpinner, FhgSpinner, GreyStripesSpinner, OriginalStripesSpinner, Spinner, SrcSpinner, WhiteStripesSpinner };
//# sourceMappingURL=index.js.map
